<template>
    <div class="total-box">
        <div class="details-content" v-if="showTaxExclude">
            <p class="price-box">
                <span class="title">{{ $t('cart.subtotal') }}:</span>
                <span class="details">
                    <del v-if="!isPriceDifference && totalRegularPrice !== subTotal ">{{currency}} {{totalRegularPrice | toFixed2}}</del>
                    <span>{{currency}} {{subTotal | toFixed2}}</span>
                </span>
            </p>
            <Discounts
                :discounts="discounts"
                :currency="currency"
            />
            <Coupon
                :discounts="discounts"
                :currency="currency"
                @addCoupon="handleAddCoupon"
                :showEdit="showCoupon"
                v-if="showCouponComp"
            />
            <p class="tax-box" v-for="(item, key) in taxData" :key="key">
                <span class="title">{{ $t( `tax.${item.type}` ) }}
                    <span v-if="Number(item.tax) && item.type !== 'us_sales'">({{ parseFloat((Number(item.rate )*100).toFixed(4)) }}%)</span>:
                </span>
                <span class="details">{{currency}} {{Number(item.tax) | toFixed2}}</span>
            </p>
        </div>
        <template v-if="!showTaxExclude">
            <Discounts
                :discounts="discounts"
                :currency="currency"
            />
            <Coupon
                :discounts="discounts"
                :currency="currency"
                @addCoupon="handleAddCoupon"
                :show-edit="showCoupon"
                v-if="showCouponComp"
            />
            <div class="line-divider"></div>
        </template>
        <div class="total-content">
            <span class="title">{{ $t('cart.total') }}:</span>
            <span class="details">{{currency}} {{ totalPrice | toFixed2 }}</span>
        </div>
        <div class="incl-tax" v-if="taxIncluded && taxData.length">
            <span class="title">
                <template v-if='VATenabled'>
                    <template v-if='shouldExcludeVAT'>
                        {{ currentTaxDetail?.name==="Incl. VAT"?$t("tax.incl_vat_tax"):''}}
                        {{ currentTaxDetail?.name==='Excl. VAT'?$t('tax.excl_vat_tax'):''}}
                    </template>
                    <!-- shouldExcludeVAT=false 无论是否为excl都收税 -->
                    <template v-else>
                        {{$t('tax.incl_vat_tax')}}
                    </template>
                </template>
                <template v-else>
                    {{$t('tax.incl_tax')}}
                </template>
                <span>({{ parseFloat((Number(currentTaxDetail?.rate )*100).toFixed(4)) }}%)</span>:
            </span>
            <span class="details">{{currency}} {{currentTaxDetail?.tax ?? 0 | toFixed2}}</span>
        </div>
    </div>
</template>

<script lang="ts">
import { IOrder, IOrderPreview, IPromotion, ITaxDetails } from '@reolink-cloud/interface';
import Vue, { PropType } from 'vue';
import Discounts from './components/Discounts.vue';
import Coupon from './components/Coupon.vue';

export default Vue.extend({
    components: {
        Discounts,
        Coupon
    },
    props: {
        previewData: {
            type: Object as PropType<IOrderPreview | undefined>
        },
        order: {
            type: Object as PropType<IOrder | undefined>,
        },
        showDetails: {
            type: Boolean,
            default: true
        },
        /**
         * 实际是是否显示coupon edit框
         */
        showCoupon: Boolean,
        /**
         * 是否显示自动续费信息
         */
        showAutoRenewal: {
            type: Boolean,
            default: false
        }
    },
    methods: {
        handleAddCoupon( code: string, type?: 'add' | 'remove', callback?: ( success: { couponError: string; hasCouponError: boolean; }) => void ) {

            this.$emit( 'addCoupon', code, type, callback );

        }
    },
    computed: {
        taxData(): ITaxDetails[] {

            if ( this.order ) {

                return this.order.taxData.details ?? [];

            } else {

                return this.previewData?.payment?.tax.details ?? [];

            }

        },
        currentTaxDetail(): ITaxDetails | undefined {

            return this.taxData[0];

        },
        taxIncluded(): boolean {

            return this.currentTaxDetail?.taxIncluded ?? false;

        },
        totalRegularPrice (): number {

            if ( this.order ) {

                return parseFloat( this.order.subTotal );

            } else {

                return parseFloat( this.previewData?.regularSubTotal ?? '0' );

            }

        },
        subTotal(): number {

            if ( this.order ) {

                return parseFloat( this.order.subTotal );

            } else {

                return  parseFloat( this.previewData?.subTotal ?? '0' );

            }

        },
        currency(): string {

            if ( this.order ) {

                return this.order.currency;

            } else {

                return this.previewData?.currency ?? '';

            }

        },

        totalPrice(): number {

            if ( this.order ) {

                return parseFloat( this.order.amount ?? '0' );

            } else {

                return parseFloat( this.previewData?.payment?.amount.value ?? '0' );

            }

        },
        VATenabled(): boolean {

            return this.currentTaxDetail?.type  === 'eu_vat';

        },
        shouldExcludeVAT(): boolean {

            // shouldExcludeVAT：true  免税开关已开启 vat号通过 total = payment.amount.value-vatTax excludeTax
            // shouldExcludeVAT：false 不免税 total = payment.amount.value includeTax
            return ( !this.currentTaxDetail?.exempted ) && ( !this.currentTaxDetail?.charged );

        },
        isPriceDifference(): boolean {

            if ( this.order ) {

                return this.order.items[0].context.adjustment ?? false;

            } else if ( this.previewData ) {

                return this.previewData.items[0].context.adjustment ?? false;

            }

            return false;

        },
        discounts(): IPromotion[] {

            return this.order?.promotion ?? this.previewData?.payment?.promotion ?? [];

        },
        showTaxExclude(): boolean {

            return !this.taxIncluded && this.taxData.length > 0 && this.showDetails;

        },
        showCouponComp(): boolean {

            return !this.isPriceDifference && this.subTotal !== 0;

        },
        isShowAutoRenewal(): boolean {

            /** 仅显示自动续费信息, 且订单每个item都开启自动续费才显示自动续费 */
            return (
                this.showAutoRenewal &&
                !!this.order?.items.every( item => item.context.autoRenew ) &&
                ( this.order.items.length > 0 )
            );

        }
    },
});
</script>

<style lang="less" scoped>
.total-box {
    text-align: right;
    margin-bottom: 60px;
    display: flex;
    flex-direction: column;
    align-items: end;

    &>div {
        width: 380px;
        padding-right: 10px;
        box-sizing: border-box;
        @media @mobile {
            width: 100%;
        }
    }

    .details-content {
        text-align: right;
        font-size: 20px;
        display: inline-block;
        padding-bottom: 20px;
        margin-bottom: 20px;
        border-bottom: 1px solid @line;

        del {
            margin-right: 14px;
            font-weight: 400;
        }

        .price-box{
            margin-bottom: 16px;
            @media @snapeak_mobile {
                margin-bottom: 0;
                display: flex;
                justify-content: space-between;
                align-items: center;
            }
        }

        @media @snapeak_mobile {
            font-size: 0.3rem;
            width: 100%;
            padding-bottom: 0.2rem;
            margin-bottom: 0.24rem;

            &:after {
                content: "";
                display: table;
                clear: both;
            }

            del {
                margin: 0 0.12rem;
            }
        }
    }

    .line-divider{
        border-bottom: 1px solid #E1E1E1;
        margin: 12px 0;
        @media @snapeak_pc {
            display: none;
        }
    }

    .total-content {
        text-align: right;
        font-size: 20px;
        font-weight: normal;
        display: inline-block;
        @media @snapeak_mobile {
            display: flex;
            justify-content: space-between;
            align-items: center;
        }

        .title {
            font-size: 20px;
            font-weight: 600;
            @media @snapeak_mobile{
                font-family: Poppins;
                font-size: 16px;
                font-weight: 500;
                line-height: 1.2;
                color: #333333;
            }
        }

        .details{
            font-size: 24px;
            font-weight: 600;
            @media @snapeak_mobile{
                font-family: Poppins;
                font-size: 16px;
                font-weight: 500;
                line-height: 1.2;
            }
        }

        @media @snapeak_mobile {
            width: 100%;
        }
    }

    .title {
        float: left;
        font-size: 16px;
    }

    .details {
        font-size: 20px;
        font-weight: 500;
        color: @red_5;
        @media @mobile {
            font-size: 14px;
            color: #333333;
        }
    }

    .incl-tax {
        text-align: right;
        font-size: 20px;
        font-weight: 400;
        display: inline-block;
        margin-top: 16px;
        box-sizing: border-box;
        .details{
            font-size: 16px;
            vertical-align: top;
        }

        @media @snapeak_mobile {
            width: 100%;
        }
    }

    .discount{
        background-color: @bg_01;
        padding: 10px;
        margin-bottom: 12px;
        &-inner {
            margin-bottom: 0;
            margin-top: 12px;
        }
        .title{
            font-size: 16px;
            font-weight: 500;
        }
        .details{
            font-size: 16px;
            font-weight: 500;
        }
    }

    .auto-renewal {
        .details {
            color: #333;
            font-size: 16px;
            font-weight: 500;
        }
    }
}
</style>
